import { Component, Input, OnInit } from '@angular/core';
import { LegendColors, PlanStatus, PlanStatusConstants, PlanStatusSelection, StatusKeys } from 'src/app/cxone/dashboard/modules/plans-status/plans-status.models';
import { PlansStatusService } from 'src/app/cxone/dashboard/modules/plans-status/plans-status.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { cloneDeep } from 'lodash';
import { NavigationService } from 'src/app/navigation/navigation.service';
import * as Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more";
@Component({
    selector: 'cv-plans-status',
    templateUrl: './plans-status.component.html',
    styleUrls: ['./plans-status.component.scss'],
})
export class PlansStatusComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    private highChartObject = null;
    private highChartObjectOnTrack = null;
    public rangeLow = 0;
    public rangeHigh = 100;
    public displayPercent = 0;
    public displayComplete = 0;
    public displayTotal = 0;
    public displayIconUrl = "";
    public autoSelect = true;
    public sortDsc = false;
    public planStatuses: PlanStatus[] = [];
    public mSelectOptions: PlanStatusSelection[] = [];
    public manualSelection: PlanStatusSelection[] = [];
    public selectedPlans: PlanStatus[] = [];
    public highcharts = Highcharts;
    public chartOptions: any;
    public highchartsOnTrack = Highcharts;
    public chartOptionsOnTrack: any;
    public isDashboardProjection = false;
    private isFirstLoad = true;
    private loadingData: boolean;
    public viewByStatusBreakDown = false;
    public plansDropdownVisibility = false;
    public chartLegends = [
        { label: "Completed", key: StatusKeys.Completed, color: LegendColors.Completed, toggleLegend: false, className: '' },
        { label: "Pending", key: StatusKeys.Pending, color: LegendColors.Pending, toggleLegend: false, className: '' },
        { label: "Deleted", key: StatusKeys.Deleted, color: LegendColors.Deleted, toggleLegend: false, className: '' },
        { label: "Appealed", key: StatusKeys.Appealed, color: LegendColors.Appealed, toggleLegend: false, className: '' },
        { label: "Expired", key: StatusKeys.Expired, color: LegendColors.Expired, toggleLegend: false, className: '' },
        { label: "Replaced", key: StatusKeys.Replaced, color: LegendColors.Replaced, toggleLegend: false, className: '' },
        { label: "Appealed Expired", key: StatusKeys.AppealedExpired, color: LegendColors.AppealedExpired, toggleLegend: false, className: '' },
        { label: "Completed Deleted", key: StatusKeys.CompletedDeleted, color: LegendColors.CompletedDeleted, toggleLegend: false, className: '' },
    ];
    private readonly colorHigh = "rgba(87, 159, 202, 1)";
    private readonly colorNormal = "rgba(232, 235, 236, 1)";
    private readonly colorLow = "rgba(224, 119, 95, 1)";

    constructor(private service: PlansStatusService,
        private cvLocalizationService: CVLocalizationService,
        private navService: NavigationService,
    ) { }

    async ngOnInit(): Promise<void> {
        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.rangeLow = Number(this.moduleManager.customSettings.MidRangeStart.getValue());
                this.rangeHigh = Number(this.moduleManager.customSettings.MidRangeEnd.getValue());
                this.toggleAutomaticAndManual(this.moduleManager.customSettings.automaticQualityPlan.getValue());
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Hour,
            onRefresh: isAutoRefresh => this.loadData(isAutoRefresh),
            canRefresh: () => !this.loadingData,
            onExport: (format, isCXOne) => this.service.exportData(this.moduleKey, format, isCXOne),
        });
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Plans_Status_Module'),
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common"),
        ];
        await Promise.all(localizationPromises);

        HC_more(this.highcharts);
        this.isDashboardProjection = this.moduleManager.container.dashboardManager.isDashboardProjection;

        //add settings so that they can be modified and saved from widget face
        this.moduleManager.addSetting({
            name: 'AutoSelect',
            getValue: () => this.autoSelect,
            onSettingLoaded: value => {
                this.autoSelect = value;
            },
            defaultValue: true,
        });
        this.moduleManager.addSetting({
            name: 'SortDsc',
            getValue: () => this.sortDsc,
            onSettingLoaded: value => { this.sortDsc = value; },
            defaultValue: false,
        });
        this.moduleManager.addSetting({
            name: 'ManualSelection',
            getValue: () => this.manualSelection,
            onSettingLoaded: value => { this.manualSelection = value; },
            defaultValue: [],
        });
        this.moduleManager.addSetting({
            name: 'ButtonSelection',
            getValue: () => this.viewByStatusBreakDown,
            onSettingLoaded: value => { this.viewByStatusBreakDown = value; },
            defaultValue: false,
        });
        this.moduleManager.customSettingsAdded = true;

        this.moduleManager.registerEvents({
            onResizeStop: () => {
                this.updateChart();
                if (this.viewByStatusBreakDown) {
                    window.setTimeout(() => this.highChartObject?.reflow(), 500);
                } else {
                    window.setTimeout(() => this.highChartObjectOnTrack?.reflow(), 500);
                }
            },
        });
    }

    async loadData(autoRefresh: boolean = false) {
        this.moduleManager.showLoader = !autoRefresh;
        if (this.loadingData)
            return;
        this.loadingData = true;

        this.planStatuses = await this.service.loadData(this.moduleKey, autoRefresh, this.isFirstLoad);
        this.mSelectOptions = [];
        this.planStatuses.forEach(e => {
            this.mSelectOptions.push({ PlanId: e.PlanId, PlanName: e.PlanName });
        });
        this.loadingData = false;
        this.isFirstLoad = false;
        this.moduleManager.showLoader = false;
        this.moduleManager.setAutoRefreshReady();
        this.updateChart();
    }

    updateChart() {
        if (this.planStatuses?.length === 0)
            return;
        this.selectedPlans.splice(0);
        if (!this.plansDropdownVisibility) {
            if (!this.viewByStatusBreakDown) {
                this.planStatuses.sort((a, b) => {
                    const temp = a.OnTrack - b.OnTrack;
                    if (temp !== 0) {
                        return temp;
                    }
                    return a.PlanName.localeCompare(b.PlanName);
                });
                const count = (this.planStatuses.length > PlanStatusConstants.maxPlanStatus) ? PlanStatusConstants.maxPlanStatus : this.planStatuses.length;
                for (let i = 0; i < count; ++i) {
                    this.selectedPlans.push(this.planStatuses[i]);
                }
            } else {
                let planStatuses = this.planStatuses;
                planStatuses.sort((a, b) => {
                    let totalStatusA = 0;
                    for (let key in StatusKeys) {
                        totalStatusA += a[StatusKeys[key]];
                    }
                    let totalStatusB = 0;
                    for (let key in StatusKeys) {
                        totalStatusB += b[StatusKeys[key]];
                    }
                    return totalStatusB - totalStatusA
                });
                const count = (planStatuses.length > PlanStatusConstants.maxPlanStatus) ? PlanStatusConstants.maxPlanStatus : planStatuses.length;
                for (let i = 0; i < count; ++i) {
                    this.selectedPlans.push(this.planStatuses[i]);
                }
            }
        } else {
            for (let i = this.manualSelection.length - 1; i > -1; --i) {
                const index = this.planStatuses.findIndex((f) => f.PlanId === this.manualSelection[i].PlanId);
                if (index === -1)
                    this.manualSelection.splice(i, 1);
                else {
                    const plan = this.planStatuses[index];
                    this.selectedPlans.push({
                        ...plan,
                        StartDate: cloneDeep(plan.StartDate),
                        EndDate: cloneDeep(plan.EndDate)
                    });

                }
            }
            if (this.viewByStatusBreakDown) {
                this.selectedPlans.sort((a, b) => {
                    let totalStatusA = 0;
                    for (let key in StatusKeys) {
                        totalStatusA += a[StatusKeys[key]];
                    }
                    let totalStatusB = 0;
                    for (let key in StatusKeys) {
                        totalStatusB += b[StatusKeys[key]];
                    }
                    return totalStatusB - totalStatusA
                });
            } else {
                this.selectedPlans.sort((a, b) => {
                    const temp = a.OnTrack - b.OnTrack;
                    if (temp !== 0) {
                        return temp;
                    }
                    return a.PlanName.localeCompare(b.PlanName);
                });
            }
        }
        if (this.autoSelect) {
            this.displayComplete = this.planStatuses.filter(ps => ps.OnTrack >= this.rangeHigh).length;
            this.displayTotal = this.planStatuses.length;
            this.displayPercent = (this.displayTotal === 0) ? 0 : (this.displayComplete / this.displayTotal) * 100;
        } else {
            this.displayComplete = this.selectedPlans.filter(ps => ps.OnTrack >= this.rangeHigh).length;
            this.displayTotal = this.selectedPlans.length;
            this.displayPercent = (this.displayTotal === 0) ? 0 : (this.displayComplete / this.displayTotal) * 100;
        }
        if (!this.viewByStatusBreakDown) {
            this.renderOnTrackChart();
        } else {
            this.renderStackChart();
        }
    }

    toggleSort() {
        this.sortDsc = !this.sortDsc;
        this.moduleManager.saveSettings();
        this.updateChart();
    }

    setAutoSelect(value: boolean = true) {
        this.viewByStatusBreakDown = value;
        this.updateChart();
        this.moduleManager.saveSettings();
        this.moduleManager.requestPublishNeeded();
    }

    openPMPage() {
        const url = `${this.navService.cxoneTokenUrls?.niceBaseUrl}/qm/monitoring/#/evaluatorAssignments`;
        window.open(url, "_blank");
    }

    renderStackChart() {
        this.loadingData = false;
        let chartData = this.selectedPlans;
        let chartOptions = {
            chart: {
                type: 'column',
            },
            colors: [],
            title: {
                text: ''
            },
            xAxis: {
                lineWidth: 1,
                gridLineWidth: 1,
                categories: chartData.map((item) => item.PlanName),
                labels: {
                    autoRotation: [-45, -90],
                    style: {
                        fontSize: '13px',
                        fontFamily: 'OpenSans-Regular, sans-serif'
                    }
                }
            },
            yAxis: {
                min: 0,
                tickInterval: 10,
                title: {
                    text: 'Number',
                    style: {
                        fontSize: '12px',
                        fontWeight: '700'
                    }
                }
            },
            legend: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                borderColor: 'rgba(210, 216, 219, 1)',
                borderRadius: 2,
                borderWidth: 1,
                formatter: function () {
                    return `<div style="font-family: 'OpenSans';">
                    <span style="display: inline-block; background-color: ${this.color}; 
                    min-width: 10px; min-height: 10px; border-radius: 50%"></span>
                    ${this.series.name}: <b>${this.y}</b></br>
                    <span style='margin-left: 14px'>Plan name: <b>${this.x}</b></span></div>`
                }
            },
            plotOptions: {
                column: {
                    pointWidth: 20,
                },
                series: {
                    stacking: 'number'
                }
            },
            series: [],
            credits: {
                enabled: false
            }
        };
        const seriesColorsData = this.generateDataForStackChart(chartData);
        chartOptions.series = [...seriesColorsData.series];
        chartOptions.colors = [...seriesColorsData.colors];
        this.chartOptions = { ...chartOptions };
        this.highChartObject?.reflow();
    }

    setChartInstance(instance) {
        if (this.viewByStatusBreakDown) {
            this.highChartObject = instance;
            window.setTimeout(() => this.highChartObject.reflow(), 0);
        } else {
            this.highChartObjectOnTrack = instance;
            window.setTimeout(() => this.highChartObjectOnTrack.reflow(), 0);
        }
    }

    generateDataForStackChart(payload: any) {
        const toggledItems = this.chartLegends.filter(item => item.toggleLegend === true);
        let aggregatedData = {};
        let colors = [];
        payload.forEach(plan => {
            Object.keys(plan).forEach((key) => {
                if (!toggledItems.some(toggledItem => toggledItem.key === key)) {
                    if (Object.values(StatusKeys).includes(key)) {
                        this.updateAggregatedData(key, plan[key], aggregatedData);
                    }
                } else if (toggledItems.some(toggledItem => toggledItem.key === key) && Object.values(StatusKeys).includes(key)) {
                    this.updateAggregatedData(key, 0, aggregatedData);
                }
            });
        });
        Object.keys(aggregatedData).forEach(keyVal => {
            colors.push(LegendColors[keyVal]);
        });
        return {
            series:
                Object.values(aggregatedData), colors: colors
        };
    }

    updateAggregatedData(key: string, value: string | number, aggregatedData: Object) {
        const label = this.getLegendsText(key);
        if (!aggregatedData[key]) {
            aggregatedData[key] = { name: label, data: [] };
        }
        aggregatedData[key].data.push(value);
    }

    toggleAutomaticAndManual(value: boolean) {
        this.planStatuses = [];
        this.selectedPlans = [];
        this.displayComplete = 0;
        this.displayTotal = 0;
        this.displayPercent = 0;
        this.autoSelect = value;
        this.plansDropdownVisibility = !value;
        if (this.viewByStatusBreakDown) {
            this.viewByStatusBreakDown = false;
        }
        this.chartLegends.map(item => {
            item.toggleLegend = false;
        });
        this.loadData(false);
        this.moduleManager.saveSettings();
    }

    selectionChanged() {
        this.updateChart();
        this.moduleManager.requestPublishNeeded();
    }

    renderOnTrackChart() {
        this.loadingData = false;
        let chartData = this.selectedPlans;
        let barColors = [];
        let barSeriesData = [];
        chartData.forEach(item => {
            barSeriesData.push([item.PlanName, item.OnTrack]);
            if (item.OnTrack <= this.rangeLow) {
                barColors.push(this.colorLow);
            } else if (item.OnTrack < this.rangeHigh && item.OnTrack > this.rangeLow) {
                barColors.push(this.colorNormal);
            } else if (item.OnTrack >= this.rangeHigh) {
                barColors.push(this.colorHigh);
            }
        });
        let chartOptions = {
            chart: {
                type: 'column'
            },
            title: {
                text: ''
            },
            credits: {
                enabled: false
            },
            xAxis: {
                type: 'category',
                lineWidth: 1,
                gridLineWidth: 1,
                labels: {
                    autoRotation: [-45, -90],
                    style: {
                        fontSize: '13px',
                        fontFamily: 'OpenSans-Regular, sans-serif'
                    }
                }
            },
            yAxis: {
                min: 0,
                max: 100,
                title: {
                    text: ''
                },
                labels: {
                    format: '{value}%'
                }
            },
            legend: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                borderColor: 'rgba(210, 216, 219, 1)',
                borderRadius: 2,
                borderWidth: 1,
                formatter: function () {
                    const pointIndex = this.point.index;
                    const plan = chartData[pointIndex];
                    const Completed = (plan.CompletedEvals < 0 || plan.TotalSegments < 1) ? "No evaluations" : `Completed: ${plan.CompletedEvals}/${plan.TotalSegments}`
                    const planEndsIn = `Plan ends in: `;
                    const days = `${plan.RemainingWrkDays} days`;
                    const onTrackPercent = `On track: ${Math.round(plan.OnTrack)}%`;
                    return `<div style="font-family: 'OpenSans';">
                        <span>${Completed}</span></br>
                        <span>${planEndsIn + days}</span></br>
                        <span>${onTrackPercent}</span>
                    </div>`
                }
            },
            plotOptions: {
                column: {
                    pointRange: 1,
                    minPointLength: 3
                }
            },
            series: [{
                name: 'On-Track Status',
                colors: barColors,
                colorByPoint: true,
                groupPadding: 0,
                pointWidth: 30,
                data: barSeriesData,
                dataLabels: {
                    enabled: true,
                    color: '#000000',
                    format: '{y:.0f}%',
                    style: {
                        fontSize: '11px',
                        fontFamily: 'OpenSans-Regular, sans-serif',
                        fontWeight: 'normal'
                    }
                }
            }]
        }
        this.chartOptionsOnTrack = { ...chartOptions };
    }

    toggleLegend(item) {
        item.toggleLegend = !item.toggleLegend;
        this.chartLegends = [...this.chartLegends];
        this.renderStackChart();
    }

    getLegendsText(key: string) {
        switch (key) {
            case StatusKeys.AppealedExpired:
                return this.cvLocalizationService.getCachedText("Plans_Status_Module", "Appealed_Expired", "Appealed Expired");
            case StatusKeys.CompletedDeleted:
                return this.cvLocalizationService.getCachedText("Plans_Status_Module", "Completed_Deleted", "Completed Deleted");
            default:
                return this.cvLocalizationService.getCachedText("Plans_Status_Module", key, key);
        }
    }
}